import type { SecureRef } from "@productlane/lib"
import {
	AGENT_TOKEN,
	isAgentType,
	parseSecureRef,
	templateTokenRegex,
} from "@productlane/lib"

export interface SecureRefOption {
	ref: string
	/** What the row shows and what a query is matched against. */
	label: string
	source: "widget" | "connector" | "contact" | "company" | "agent"
	connectorTitle?: string
	sectionId?: string
	sample: string | null
	hidden?: boolean
	remembered?: boolean
	ambiguous?: boolean
}

const TOKEN_OPEN = "{{"
const TOKEN_CLOSE = "}}"

export interface StaleRef {
	/** The bare `http.<key>` the rule still holds. */
	ref: string
	/** Connectors that send this key, so the rule can be pointed at one. */
	matches: SecureRefOption[]
}

/**
 * Bare `http.<key>` refs a rule still holds. They resolve against the connector
 * being called, which is the MCP server the policy sits on, so they never run.
 */
export function staleHttpRefs(
	value: string,
	mode: "ref" | "template",
	options: readonly SecureRefOption[],
): StaleRef[] {
	const raws = refsIn(value, mode)

	const stale: StaleRef[] = []
	for (const raw of raws) {
		const key = /^http\.([A-Za-z][A-Za-z0-9_-]{0,63})$/.exec(raw)?.[1]
		if (!key) continue
		if (stale.some((entry) => entry.ref === raw)) continue
		stale.push({
			ref: raw,
			matches: options.filter(
				(option) =>
					option.source === "connector" && option.label === `http.${key}`,
			),
		})
	}
	return stale
}

function refsIn(value: string, mode: "ref" | "template"): string[] {
	return mode === "ref"
		? [value.trim()]
		: [...value.matchAll(templateTokenRegex())].map((match) => match[1]!.trim())
}

/**
 * Whether the active token owns the slot it sits in, which is what a type needs to
 * survive: the whole value, or a whole JSON string leaf.
 */
export function tokenFillsSlot(value: string, token: ActiveToken): boolean {
	const before = value.slice(0, token.start)
	const after = value.slice(token.end)
	if (!before.trim() && !after.trim()) return true
	return before.endsWith('"') && after.startsWith('"')
}

/** Whether a step reads verified widget data, which only a widget visitor carries. */
export function usesWidgetData(
	value: string,
	mode: "ref" | "template",
): boolean {
	return refsIn(value, mode).some((raw) => raw.startsWith("widget."))
}

const TYPED_HTTP_REF = /^http\.([A-Za-z0-9-]{8,64})(?:\.(.*))?$/
const TYPED_DISPLAY_REF = /^http\."(.+)"(?:\.(.*))?$/

/**
 * Splits a half typed `http.<connectorId>.<key>` or `http."<name>".<key>` into
 * the connector it names and the key being typed, so the list narrows to that
 * connector and still matches on the key rather than on the id or the name.
 */
export function splitTypedRef(
	raw: string,
	namesToIds?: ReadonlyMap<string, string>,
): {
	sectionId: string | null
	query: string
} {
	const trimmed = raw.trim()
	const named = TYPED_DISPLAY_REF.exec(trimmed)
	const namedId = named ? namesToIds?.get(named[1]!) : undefined
	if (named && namedId) return { sectionId: namedId, query: named[2] ?? "" }

	const match = TYPED_HTTP_REF.exec(trimmed)
	if (!match) return { sectionId: null, query: raw }
	return { sectionId: match[1]!, query: match[2] ?? "" }
}

/** Connector ids the value names, for labelling a ref whose id is collapsed. */
export function referencedConnectors(
	value: string,
	mode: "ref" | "template",
): string[] {
	return refsIn(value, mode)
		.map((raw) => parseSecureRef(raw))
		.filter(
			(ref): ref is Extract<SecureRef, { namespace: "http" }> =>
				ref?.namespace === "http" && !!ref.sectionId,
		)
		.map((ref) => ref.sectionId!)
}

export function replaceRef(
	value: string,
	mode: "ref" | "template",
	from: string,
	to: string,
): string {
	if (mode === "ref") return to
	return value.replace(templateTokenRegex(), (token, raw: string) =>
		raw.trim() === from ? `${TOKEN_OPEN}${to}}}` : token,
	)
}

export interface ActiveToken {
	start: number
	end: number
	query: string
}

/** The `{{` token the caret sits inside, or null when it sits in literal text. */
export function activeTokenAt(
	value: string,
	caret: number,
): ActiveToken | null {
	const open = value.lastIndexOf(TOKEN_OPEN, Math.max(caret - 1, 0))
	if (open === -1) return null
	const close = value.indexOf("}}", open)
	if (close !== -1 && close + 2 <= caret) return null
	return {
		start: open,
		end: close === -1 ? caret : close + 2,
		query: value.slice(open + 2, caret).trim(),
	}
}

export interface RefEdit {
	value: string
	caret: number
	/** Text to leave selected, so the next keystroke replaces it. */
	select?: { start: number; end: number }
}

/**
 * Places a picked reference. Template mode replaces the token the caret is in and
 * otherwise splices a new one in, so picking mid-text does not append at the end.
 */
export function insertRef(
	value: string,
	caret: number,
	ref: string,
	mode: "ref" | "template",
): RefEdit {
	if (mode === "ref") return { value: ref, caret: ref.length }

	const token = activeTokenAt(value, caret)
	const start = token ? token.start : caret
	const end = token ? token.end : caret
	const insert = `${TOKEN_OPEN}${ref}}}`
	return {
		value: `${value.slice(0, start)}${insert}${value.slice(end)}`,
		caret: start + insert.length,
	}
}

/**
 * The typed ref for a property of the agent's value: `agent.<type>` reads the whole
 * value, which an object argument never is. Null for anything else, the bare
 * `agent` row and `agent.json` included, since both do read the whole value.
 */
export function agentPropertyRef(ref: string, property: string): string | null {
	const parts = ref.split(".")
	if (parts.length !== 2 || parts[0] !== AGENT_TOKEN) return null
	const type = parts[1]!
	if (!isAgentType(type) || type === "json") return null
	return `${AGENT_TOKEN}.${property}.${type}`
}

/** Inserts a property token with the property name selected, ready to be renamed. */
export function insertAgentProperty(
	value: string,
	caret: number,
	ref: string,
	mode: "ref" | "template",
): RefEdit {
	const edit = insertRef(value, caret, ref, mode)
	// The caret lands after the token, so the ref starts one closing brace back.
	const refStart =
		edit.caret - ref.length - (mode === "ref" ? 0 : TOKEN_CLOSE.length)
	const start = refStart + AGENT_TOKEN.length + 1
	const property = ref.slice(AGENT_TOKEN.length + 1, ref.lastIndexOf("."))
	return { ...edit, select: { start, end: start + property.length } }
}

/**
 * The `agent[.property]` head a query names a type on, or null when it already names
 * one: a token carries a single type, so nothing follows it. Empty query reads as
 * `agent`. A bracket-quoted `["integer"]` is a property name, not a type.
 */
export function agentTypeHead(query: string): string | null {
	const trimmed = query.trim()
	if (trimmed !== AGENT_TOKEN && !trimmed.startsWith(`${AGENT_TOKEN}.`)) {
		return AGENT_TOKEN
	}
	const body = trimmed.slice(AGENT_TOKEN.length)
	const lastDot = body.lastIndexOf(".")
	// A trailing segment is the type being typed, so only what precedes it is a path.
	const path = lastDot > 0 ? body.slice(1, lastDot) : ""
	if (!path) return AGENT_TOKEN
	return isAgentType(path.slice(path.lastIndexOf(".") + 1))
		? null
		: `${AGENT_TOKEN}.${path}`
}

function scoreRef(ref: string, needle: string): number {
	if (ref === needle) return 4
	if (ref.startsWith(needle)) return 3
	// Typing past the dot is the common case, so match the key on its own too.
	const key = ref.slice(ref.lastIndexOf(".") + 1)
	if (key.startsWith(needle)) return 2
	return ref.includes(needle) ? 1 : 0
}

export function rankRefOptions(
	options: readonly SecureRefOption[],
	query: string,
	limit = 50,
): SecureRefOption[] {
	const needle = query.trim().toLowerCase()
	if (!needle) return options.slice(0, limit)

	return options
		.map((option, index) => ({
			option,
			index,
			// Matched on the label, so a connector id in the ref cannot score.
			score: scoreRef(option.label.toLowerCase(), needle),
		}))
		.filter((entry) => entry.score > 0)
		.sort((a, b) => b.score - a.score || a.index - b.index)
		.slice(0, limit)
		.map((entry) => entry.option)
}

export function nextActiveIndex(
	current: number,
	count: number,
	key: "ArrowDown" | "ArrowUp",
): number {
	if (count === 0) return -1
	if (key === "ArrowDown") return current >= count - 1 ? 0 : current + 1
	return current <= 0 ? count - 1 : current - 1
}
